import React, { useEffect, useState } from 'react';
import { mixGetV2, mixPostV2, formatNumber, mixPostV1, mixGetV1, mixGetV3, mixSynStatus, mixDataStatus } from '../../services/mixin';
import Layout from '../../Layout';
import { Input, Pagination, Select, Tag } from 'antd';
import { UploadOutlined, EyeOutlined, SettingOutlined, MergeOutlined } from '@ant-design/icons';
import type { SearchProps } from 'antd/es/input/Search';
import './CustomerList.scss';
import { Link, useNavigate } from 'react-router-dom';
import useToast from '../../hook/useToast';
import BreadcrumbBlock from '../../components/BreadcrumbBlock/BreadcrumbBlock';

import MegaTable from '../../components/MagaTableV2';
import { selectorInfoUser } from '../../store/slices/infoUserSlice';
import { selectorUnitByUserPermission } from '../../store/slices/UnitByUserPermission';
import { selectorSourceColumnWarehouse } from '../../store/slices/sourceColumnWarehouseSlice';
import { useSelector } from 'react-redux';

const getDefaultPagination = () => ({ current: 1, pageSize: 10, total: 0 });

const CustomerList: React.FC = () => {
  const pushToast = useToast();
  const navigate = useNavigate()
  const user = useSelector(selectorInfoUser);
  const unitByUserPermission = useSelector(selectorUnitByUserPermission);
  const sourceColumnWarehouse = useSelector(selectorSourceColumnWarehouse);

  const [parentUnitCode, setParentUnitCode] = useState<string>('');
  useEffect(() => {
    const findData = unitByUserPermission.find(x => x.unitId === user.unitTemp) as any
    setParentUnitCode(findData ? findData.unitCode : '')
  }, [user, unitByUserPermission]);

  useEffect(() => {
    if (parentUnitCode) {
      getUnitList(parentUnitCode)
    }
  }, [parentUnitCode]);

  const tableColumns = (sourceColumnWarehouse: any) => {
    const temp = [] as any
    sourceColumnWarehouse.customer?.list.map((col: any) => {
      temp.push({
        headerName: col.headerName,
        field: col.field,
        tooltipField: col.field,
      })
    })
    return temp
  }

  // State
  const [megaTableData, setMegaTableData] = useState<any>({
    name: 'TbCachePoolingDateCustomerList',
    col: [
      {
        headerName: 'Hành động',
        field: 'action',
        width: 100,
        resizable: false,
        sortable: false,
        filter: false,
        lockPosition: 'left',
        cellRenderer: (params: any) => {
          return (
            <div className='list-action-icon'>
              <Link className='action-ctr' to={`/customer/pooling-data/customer-list/detail?id_sync=${params.data.id}`}>
                <EyeOutlined />
              </Link>
            </div>
          )
        },
      },
      {
        headerName: 'Trạng thái',
        field: 'data_status',
        cellRenderer: (params: any) => {
          return (
            <div>
              <Tag color={mixDataStatus(params.data.data_status).color}>
                {mixDataStatus(params.data.data_status).title}
              </Tag>
            </div>
          )
        }
      },
      {
        headerName: 'Trạng thái đồng bộ',
        field: 'synced_status',
        cellRenderer: (params: any) => {
          return (
            <div>
              <Tag color={mixSynStatus(params.data.synced_status).color}>
                {mixSynStatus(params.data.synced_status).title}
              </Tag>
            </div>
          )
        }
      },
      ...tableColumns(sourceColumnWarehouse),
    ],
    listData: [],
    objSelectData: {},
    objSelect: true,
    showSetting: false,
    loading: true,
    height: '500px'
  });
  const [pagination, setPagination] = useState(getDefaultPagination());
  const [defaultKeyword, setDefaultKeyword] = useState<string>('');
  const [dasData, setDasData] = useState<any>({
    new_customers: 0,
    updated_customers: 0,
  } as any);
  const [paramListData, setParamListData] = useState<any>({
    'sort[]': 'created,desc',
    page: 1,
    limit: 10,
    keyword: '',
    branch_code: null,
    data_status: null,
    synced_status: null,
    ready: false,
  });

  const [listSelectData, setListSelectData] = useState<any[]>([]);
  useEffect(() => {
    const temp = [] as any[];
    Object.keys(megaTableData.objSelectData).forEach((key) => {
      if (megaTableData.objSelectData[key]) {
        temp.push(megaTableData.objSelectData[key]);
      }
    });
    setListSelectData(temp);
  }, [megaTableData.objSelectData]);

  // Load URL query params into state on mount
  useEffect(() => {
    const search = window.location.search;
    if (!search) return;
    const sp = new URLSearchParams(search);
    const parsed: any = {};

    for (const [key, value] of sp.entries()) {
      // preserve array-like keys (e.g. sort[]). Keep exact key name as used elsewhere.
      if (key.endsWith('[]')) {
        if (!parsed[key]) parsed[key] = [];
        parsed[key].push(value);
      } else if (Object.prototype.hasOwnProperty.call(parsed, key)) {
        // multiple entries for same key => convert to array
        if (!Array.isArray(parsed[key])) parsed[key] = [parsed[key]];
        parsed[key].push(value);
      } else {
        parsed[key] = value;
      }
    }

    // coerce numeric values
    if (parsed.page) parsed.page = parseInt(parsed.page as string, 10) || 1;
    if (parsed.limit) parsed.limit = parseInt(parsed.limit as string, 10) || 10;
    if (parsed.data_status) parsed.data_status = parseInt(parsed.data_status as string, 10);
    if (parsed.synced_status) parsed.synced_status = parseInt(parsed.synced_status as string, 10);

    // Apply parsed values to paramListData and pagination
    setDefaultKeyword(parsed.keyword || '');
    setParamListData((prev: any) => ({ ...prev, ...parsed }));
    setPagination((prev) => ({
      ...prev,
      current: parsed.page || prev.current,
      pageSize: parsed.limit || prev.pageSize,
    }));
  }, []);

  // Search
  const { Search } = Input;
  const onSearch: SearchProps['onSearch'] = (value) => {
    setParamListData((prev: any) => ({
      ...prev,
      keyword: value,
      page: 1,
      limit: pagination.pageSize
    }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  // Pagination
  const handleChangePagination = (current: number, pageSize: number) => {
    setPagination({ ...pagination, current, pageSize });
    setParamListData((prev: any) => ({ ...prev, limit: pageSize, page: current }));
  };

  const getListData = async () => {
    setMegaTableData((prev: any) => ({ ...prev, loading: true }));
    const params = new URLSearchParams();
    Object.entries(paramListData).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined || key === 'ready') return;
      if (Array.isArray(value)) {
        value.forEach(v => params.append(key, String(v)));
      } else {
        if (String(value) === '') return;
        params.set(key, String(value));
      }
    });
    navigate(`${window.location.pathname}?${params.toString()}`, { replace: true });

    const listCodeUnit = await getUnitListForSearch(paramListData.branch_code || parentUnitCode)
    const res = await mixGetV2('/api/v1/be/warehouses_elastic_v2', {
      ...paramListData,
      branch_code: paramListData.branch_code ? listCodeUnit : undefined,
      admin_id: user.id,
    })
    setMegaTableData((prev: any) => ({ ...prev, loading: false }));
    if (res.status) {
      const temp = [] as any;
      res.data.items.forEach((val: any) => {
        temp.push({
          ...val,
          key: val.id,
          hideCheckBox: val.synced_status === 2, // Disable checkbox if already synced
        })
      });
      setDasData({ ...res.data.sumary })
      setMegaTableData((prev: any) => ({ ...prev, listData: temp }));
      setPagination((prev) => ({ ...prev, total: res.data.total }));
    }
  }

  const getListDataTest = async () => {
    setMegaTableData((prev: any) => ({ ...prev, loading: true }));
    const params = new URLSearchParams();
    Object.entries(paramListData).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined) return;
      if (Array.isArray(value)) {
        value.forEach(v => params.append(key, String(v)));
      } else {
        if (String(value) === '') return;
        params.set(key, String(value));
      }
    });
    navigate(`${window.location.pathname}?${params.toString()}`, { replace: true });
    const res = await mixGetV3(`/customer_v2_for_merge/?skip=${(paramListData.page - 1) * paramListData.limit}&limit=${paramListData.limit}`)
    setMegaTableData((prev: any) => ({ ...prev, loading: false }));
    if (res.count) {
      const temp = [] as any;
      res.data.forEach((val: any) => {
        temp.push({
          ...val,
          key: val.id,
        })
      });
      setMegaTableData((prev: any) => ({ ...prev, listData: temp }));
      setPagination((prev) => ({ ...prev, total: res.count }));
    }
  }
  // Fetch List Data
  useEffect(() => {
    if (paramListData.ready) {
      getListData();
    }
  }, [paramListData]);

  // Syndata muti
  const mutiSync = async () => {
    setMegaTableData((prev: any) => ({ ...prev, loading: true }));
    const ids = listSelectData.map((item: any) => item.id);
    const param = {
      customerIds: ids,
      userId: user.id,
    };
    const res = await mixPostV1('cronJobs/syncDataCustomerV3', param);
    if (res.errors.length === 0) {
      penddingSyncedStatus();
    } else {
      setMegaTableData((prev: any) => ({ ...prev, loading: false }));
      pushToast(res.message, 'error');
    }
  };

  // Syndata Unit
  const penddingSyncedStatus = async () => {
    const ids = listSelectData.map((item: any) => item.id);
    const param = {
      ids_customer: ids.join(','),
      userId: user.id,
    };
    const res = await mixPostV2('/api/v1/be/customer/pendding_synced_status', param);
    if (res.status) {
      setMegaTableData((prev: any) => ({ ...prev, objSelectData: {} }));
      pushToast(`Đã tiếp nhận (${ids.length}) dữ liệu khách hàng xử lý đồng bộ dữ liệu qua CRM thành công`, "success");
      setParamListData((prev: any) => ({ ...prev, ready: true }));
    } else {
      setMegaTableData((prev: any) => ({ ...prev, loading: false }));
      pushToast('Cập nhật trạng thái đang đồng bộ không thành công!', 'error');
    }
  };

  let wait3 = false
  const [unitList, setUnitList] = useState<any>([])
  const getUnitList = async (codeUnit = '') => {
    if (wait3) {
      return
    }
    wait3 = true
    const res = await mixGetV1(`unit/getChildUnits?codeUnit=${codeUnit}`);
    wait3 = false
    if (res.status) {
      const findData = res.data.find((x: any) => x.code === paramListData.branch_code)
      if (!findData) {
        setParamListData((prev: any) => ({ ...prev, branch_code: null, ready: true }));
      } else {
        setParamListData((prev: any) => ({ ...prev, branch_code: findData.code, ready: true }));
      }
      setUnitList(res.data || [])
    }
  }

  const getUnitListForSearch = async (codeUnit = '') => {
    const res = await mixGetV1(`unit/getChildUnits?codeUnit=${codeUnit}`);
    wait3 = false
    if (res.status) {
      const listCodeUnit = res.data.map((item: any) => item.code)
      return listCodeUnit.join(',')
    }
  }

  return (
    <>
      <Layout className='customer-list'>
        <BreadcrumbBlock
          items={[
            { title: <Link to="/">THACO AUTO CRM</Link> },
            { title: 'Khách hàng' },
            { title: <Link to="/customer/pooling-data">Gộp dữ liệu khách hàng</Link> },
            { title: 'Danh sách khách hàng đã được làm sạch' },
          ]}
          title={`Danh sách khách hàng đã được làm sạch (${formatNumber(pagination.total)} khách hàng)`}
          backLink='/customer/pooling-data'
          className='gutter-block-end'
        />

        <div className="his-total-list v2">
          <div className="his-one-item" style={{ backgroundImage: 'url("/images/bg-item.svg")' }}>
            <div className="title">Mới</div>
            <div className="count">{dasData.new_customers ? formatNumber(dasData.new_customers) : 0}</div>
          </div>
          <div className="his-one-item" style={{ backgroundImage: 'url("/images/bg-item.svg")', borderColor: '#F59C23' }}>
            <div className="title">Cập nhật</div>
            <div className="count">{dasData.updated_customers ? formatNumber(dasData.updated_customers) : 0}</div>
          </div>
        </div>

        {/* Filter block */}
        <div className={`filter-block ${listSelectData.length ? "active" : ""}`}>
          <div className='t-search-block v2'>
            <Search value={defaultKeyword} placeholder="Tìm kiếm" onSearch={onSearch} onChange={(e) => {
              setDefaultKeyword(e.target.value)
            }} />
            <Select
              className='rs-select v2'
              placeholder='Chọn trạng thái'
              allowClear
              options={mixDataStatus().map((status: any) => ({ label: status.title, value: status.value }))}
              value={paramListData.data_status}
              onChange={(value) => {
                setParamListData((prev: any) => ({ ...prev, data_status: value, page: 1 }));
                setPagination((prev) => ({ ...prev, current: 1 }));
              }}
            />
            <Select
              className='rs-select v2'
              placeholder='Chọn trạng thái đồng bộ'
              allowClear
              options={mixSynStatus().map((status: any) => ({ label: status.title, value: status.value }))}
              value={paramListData.synced_status}
              onChange={(value) => {
                setParamListData((prev: any) => ({ ...prev, synced_status: value, page: 1 }));
                setPagination((prev) => ({ ...prev, current: 1 }));
              }}
            />
            <Select
              className='rs-select v2'
              placeholder='Chọn đơn vị'
              showSearch
              allowClear
              filterOption={(input, option) => {
                const normalize = (s: any) =>
                  String(s ?? '')
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .replace(/đ/g, 'd')
                    .replace(/Đ/g, 'D')
                    .toLowerCase()

                const label = option?.label ?? option?.children ?? option?.value ?? ''
                return normalize(label).includes(normalize(input))
              }}
              options={unitList ? unitList.map((val: any) => ({ label: val.title, value: val.code })) : []}
              value={paramListData.branch_code}
              onChange={(value) => {
                setParamListData((prev: any) => ({ ...prev, branch_code: value, page: 1 }));
                setPagination((prev) => ({ ...prev, current: 1 }));
              }}
            />
          </div>
          <div className="btn-input" onClick={() => navigate('/customer/pooling-data/import-data')}>
            <div className="icon-svg"><UploadOutlined /></div>
            <div className="text">Nhập DS</div>
          </div>
          <div className="btn-input btn-primary" onClick={() => navigate('/customer/pooling-data/pooling-data-auto')}>
            <div className="icon-svg"><MergeOutlined /></div>
            <div className="text">Gộp dữ liệu</div>
          </div>
          <div className="btn-input t-px-8" onClick={() => { setMegaTableData((prev: any) => ({ ...prev, showSetting: true })) }}>
            <div className="icon-svg"><SettingOutlined /></div>
          </div>
        </div>

        {/* Filter block for selected */}
        <div className={`filter-block filter-active-show ${listSelectData.length ? "active" : ""}`}>
          <div className='total-chosse'>
            {listSelectData.length ? `Đã chọn ${listSelectData.length} khách hàng` : ''}
          </div>
          <div className="btn-input btn-primary" onClick={() => { mutiSync() }}>
            <div className="text">Đồng bộ dữ liệu sang CRM</div>
          </div>
          <div className="btn-input border-text-primary" onClick={() => navigate('/customer/pooling-data/customer-list/manual-data-merging?ids=' + listSelectData.map((item: any) => item.id).join(','))}>
            <div className="text">Gộp dữ liệu thủ công</div>
          </div>


          {/* {paramListData.branch_code &&
            <div className="btn-input" onClick={() => { unitSync() }}>
              <div className="text">
                Đồng bộ dữ liệu đơn vị {paramListData.branch_code ? `(${(unitList.find((u: any) => u.unitCode === paramListData.branch_code)?.unitTitle ?? paramListData.branch_code)})` : ''} sang CRM
              </div>
            </div>
          } */}

        </div>
        <MegaTable
          stData={{ ...megaTableData }}
          stChange={(data) => {
            setMegaTableData((prev: any) => ({ ...prev, ...data }));
          }}
        />

        <div className="t-table-block t-table-customer c-table-scoll-x">
          <div className="pagination-block">
            <Pagination
              className='rs-pagination'
              showSizeChanger
              total={pagination.total}
              defaultPageSize={pagination.pageSize}
              current={pagination.current}
              pageSizeOptions={['10', '20', '50', '100']}
              itemRender={(page, type, originalElement) => {
                // Chỉ custom khi là nút số trang
                if (type === 'page') {
                  return <span>{formatNumber(page)}</span>;
                }
                // prev / next / jump-prev / jump-next giữ nguyên
                return originalElement;
              }}
              showTotal={() => `Tổng cộng ${Math.ceil(pagination.total / pagination.pageSize) === 0 ? 1 : Math.ceil(pagination.total / pagination.pageSize)} trang`}
              locale={{ items_per_page: 'trang' }}
              onChange={handleChangePagination}
            />
          </div>
        </div>
      </Layout>
    </>
  );
};

export default CustomerList;
